import { createFileRoute } from '@tanstack/react-router'
import PageHeader from '@/components/settings/PageHeader'
import { useBlockedUsers, useUnblockUser } from '@/hooks/api/useBlocks'
import { Button } from '@/components/shared/atoms/Button'
import { useToast } from '@/components/shared/toast/Toast'
import { Link } from '@tanstack/react-router'
import { captureException } from '@/config/sentry'

export const Route = createFileRoute('/settings/blocked-users')({
    component: BlockedUsersPage,
})

function BlockedUsersPage() {
    const { data: blockedUsersData, isLoading } = useBlockedUsers()
    const { mutateAsync: unblockUser, isPending: isUnblocking } = useUnblockUser()
    const { addToast } = useToast()

    const blockedUsers = blockedUsersData?.blockedUsers ?? []

    const handleUnblock = async (userId: string, username: string) => {
        try {
            await unblockUser(userId)
            addToast(`@${username} has been unblocked`, 'success')
        } catch (error) {
            captureException(error, { action: 'unblock_user', userId, username })
            addToast('Failed to unblock user. Please try again.', 'error')
        }
    }

    return (
        <div className="min-h-dvh bg-white px-2 pb-20">
            <PageHeader title="Blocked Users" />

            <div className="mt-4 space-y-2">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="text-gray-500">Loading...</div>
                    </div>
                ) : blockedUsers.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                        <div className="mb-2 text-lg font-medium text-gray-900">
                            No blocked users
                        </div>
                        <p className="text-sm text-gray-500">
                            Users you block will appear here. You can unblock them at any time.
                        </p>
                    </div>
                ) : (
                    blockedUsers.map((blockedUser) => (
                        <div
                            key={blockedUser.id}
                            className="flex items-center justify-between rounded-2xl border border-gray-100 p-4"
                        >
                            <Link
                                to="/profiles/$id"
                                params={{ id: blockedUser.id }}
                                className="flex items-center gap-3"
                            >
                                {blockedUser.avatarUrl ? (
                                    <img
                                        src={blockedUser.avatarUrl}
                                        alt={blockedUser.username}
                                        className="h-12 w-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-200 text-gray-500">
                                        {blockedUser.username?.charAt(0)?.toUpperCase() ?? '?'}
                                    </div>
                                )}
                                <div>
                                    <div className="font-medium text-gray-900">
                                        @{blockedUser.username}
                                    </div>
                                    {blockedUser.name && (
                                        <div className="text-sm text-gray-500">
                                            {blockedUser.name}
                                        </div>
                                    )}
                                </div>
                            </Link>
                            <Button
                                variant="outline"
                                onClick={() => handleUnblock(
                                    blockedUser.id,
                                    blockedUser.username
                                )}
                                disabled={isUnblocking}
                                className="border-gray-300 text-sm"
                            >
                                Unblock
                            </Button>
                        </div>
                    ))
                )}
            </div>
        </div>
    )
}
